import type { ChatMessage } from "../types/chatMessageTypes";

export interface ChatQueryData {
  pages: Array<{
    messages: ChatMessage[];
    hasMore: boolean;
    beforeCreated: string;
    users?: Array<{
      userId: number;
      name: string;
    }>;
  }>;
  pageParams: unknown[];
}

/**
 * Adds a new message to the chat message cache
 * @param oldData The existing query data from the cache
 * @param newMessage The new message to add
 * @returns Updated query data or null if no update needed
 */
export function addToChatMessageCache(
  oldData: ChatQueryData | undefined,
  newMessage?: ChatMessage,
): ChatQueryData | undefined {
  if (!oldData || !newMessage) return oldData;

  // Add the new message to the first page
  const newPages = [...oldData.pages];
  if (newPages[0]) {
    // Check if message already exists in the cache. We are ok
    // just checking the first page here because these are only new
    // messages that we are getting WS notifications for.
    const messageExists = newPages[0].messages.some((msg) => {
      const found =
        msg.uuid === newMessage.uuid ||
        // Some messages don't have a clientUuid, so check if it exists and it matches an already existing message
        (msg.clientUuid && msg.clientUuid === newMessage.clientUuid);

      return found;
    });

    // Only add if message doesn't exist
    if (!messageExists) {
      newPages[0] = {
        ...newPages[0],
        messages: [...newPages[0].messages, newMessage],
      };
    }
  }

  return {
    ...oldData,
    pages: newPages,
  };
}

export function updateChatMessageCache(
  oldData: ChatQueryData | undefined,
  updateMessage: ChatMessage,
): ChatQueryData | undefined {
  if (!oldData) return oldData;

  const newPages = oldData.pages.map((page) => {
    // Efficiently check if the message exists and update it in a single pass
    let messageUpdated = false;
    const updatedMessages = page.messages.map((msg) => {
      if (msg.uuid === updateMessage.uuid) {
        messageUpdated = true;
        return updateMessage;
      }
      return msg;
    });

    // Only create a new page object if something changed
    return messageUpdated ? { ...page, messages: updatedMessages } : page;
  });

  return {
    ...oldData,
    pages: newPages,
  };
}
